import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Globe } from "@phosphor-icons/react"
import { buttonVariants } from "@/components/ui/button"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { CustomSofiaEvent, sofiaEventDispatch } from "@/hooks/useSofiaEventListener"
import { useToast } from "@/hooks/useToast"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { cn } from "@/lib/utils"
import { useAppendChatHistoryMessage } from "@/modules/chats/hooks/useChatHistory"
import { showAIErrorMessages } from "@/modules/chats/lib/show-ai-error-messages"
import { translateQuery } from "@/modules/chats/services/ai/translateQuery"
import { useChatInputContext } from "@/modules/chats/stores/useChatInputStore"
import { AiTrackingMetadata, CHAT_MESSAGE_SEPARATOR } from "@/modules/chats/types/chat-message"
import { ChatThread } from "@/modules/chats/types/chat-thread"

export const TranslateButton = (props: { selectedThread: ChatThread }) => {
  const { selectedThread } = props
  const { t } = useTranslation()
  const { toast } = useToast()

  const isAiRunning = useChatInputContext((store) => store.aiRunning !== "NONE")
  const isSendingMessage = useChatInputContext((store) => store.isSendingMessage)
  const messageText = useChatInputContext((store) => store.messageText)
  const setAiRunning = useChatInputContext((store) => store.setAiRunning)
  const setAiTrackingMetadata = useChatInputContext((store) => store.setAiTrackingMetadata)
  const appendHistory = useAppendChatHistoryMessage()

  const isLiveTranslationEnabled = selectedThread.autoTranslationSetting?.isLiveTranslationEnabled ?? false

  const handleTranslate = useCallback(async () => {
    try {
      setAiRunning("TRANSLATE")
      sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", true))
      const aiFeatureId = uuidv4()
      const result = await translateQuery(selectedThread.patient.country, {
        messageRole: "DOCTOR",
        aiRequestMetadata: {
          aiFeatureId,
          appointmentId: selectedThread.appointmentId,
          age: selectedThread.patient.age.years,
          gender: selectedThread.patient.gender,
          userInput: messageText,
        },
      })

      if (!result) {
        return
      }
      if (result.translate.result === "SUCCESS") {
        appendHistory(result.translate.text, "TRANSLATE", messageText)

        FaroInsightsWrapper.pushEvent("aiFeature_translate", {
          aiFeatureId,
          feature: "Translate",
          fileHash: result.translate.fileHash,
          aiRequestDurationMs: result.translate.aiRequestDurationMs.toString(),
          aiAnonymizationRequestDurationMs: result.translate.aiAnonymizationRequestDurationMs.toString(),
          sentAt: new Date().toISOString(),
        })

        const trackingMetadata: AiTrackingMetadata = {
          id: aiFeatureId,
          name: "Translate",
          previousText: messageText,
          newText: result.translate.text,
          aiFeature: "TRANSLATE",
          fileHash: result.translate.fileHash,
          aiRequestDurationMs: result.translate.aiRequestDurationMs,
          aiAnonymizationRequestDurationMs: result.translate.aiAnonymizationRequestDurationMs,
          createdAt: new Date().toISOString(),
        }
        setAiTrackingMetadata([trackingMetadata])
      } else {
        showAIErrorMessages(result.translate.result)
      }
    } catch {
      toast({ description: t("chat.ai.errors.UNKNOWN_ERROR") })
    } finally {
      setAiRunning("NONE")
      sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", false))
    }
  }, [messageText, selectedThread, t, toast, setAiRunning, setAiTrackingMetadata, appendHistory])

  return (
    <TooltipCustom>
      <TooltipTrigger asChild>
        <button
          data-testid="translate-chat-input"
          className={cn(
            buttonVariants({ variant: "circle" }),
            "h-8 w-8 disabled:cursor-not-allowed disabled:bg-transparent",
            {
              "line-through opacity-50": isLiveTranslationEnabled,
            },
          )}
          disabled={isAiRunning || isSendingMessage || messageText.trim().length < 2 || isLiveTranslationEnabled}
          onClick={handleTranslate}>
          <Globe size={24} />
        </button>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="bottom" align="start">
          {t("chat.translateTo") +
            CHAT_MESSAGE_SEPARATOR.SPACE +
            t(`common.languageList.${selectedThread.language.toLowerCase()}`)}
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}
